/**
 * Markdown-to-HTML rendering shared by every page that renders repository
 * markdown as a page: the format spec today, the Help guides alongside it.
 *
 * Heading ids come from `github-slugger`, which implements GitHub's own
 * slugging rules, so deep links people already hold to the spec
 * (`#82-snapshot-records`) keep resolving, and a guide's anchors behave the
 * same way from the day it ships. `markdown.test.ts` pins one of the spec's
 * slugs for that reason.
 */

import GithubSlugger from "github-slugger";
import { Renderer, parse } from "marked";

export interface Heading {
  readonly depth: number;
  readonly text: string;
  readonly id: string;
}

/**
 * All of this renderer's input is repository content, written by us and
 * fixed at build time — no user input ever reaches it, which is what makes
 * `dangerouslySetInnerHTML` the right call at the call sites rather than
 * pulling in a sanitiser for a constant.
 */
export function renderMarkdown(markdown: string): { html: string; headings: Heading[] } {
  const slugger = new GithubSlugger();
  const headings: Heading[] = [];
  // Every document opens with its own title, which the page header above it
  // already states. Rendering both would put two `h1`s on the page and read
  // as a stutter, so the source's title line is dropped and its `## `
  // sections sit directly under the page heading.
  const body = markdown.replace(/^#\s+.*(?:\r?\n)+/, "");

  const renderer = new Renderer();
  renderer.heading = function heading({ tokens, depth }) {
    const html = this.parser.parseInline(tokens);
    // The contents list and the slugger both want the heading as a person
    // reads it, and `parseInline` hands back HTML — so the tags come off and
    // the entities go back to being the characters they stand in for. Without
    // that second step a heading with an apostrophe in it lists itself as
    // `it&#39;s` and anchors at `it39s`, which is not a heading anybody would
    // think to avoid writing.
    const text = plainText(html);
    const id = slugger.slug(text);
    // Only the two levels a table of contents offers need to be collected;
    // deeper ones still get ids so their anchors work.
    if (depth <= 3) headings.push({ depth, text, id });
    return `<h${depth} id="${id}">${html}</h${depth}>\n`;
  };

  const html = parse(body, { renderer, async: false });
  return { html, headings };
}

/**
 * The named references marked escapes on its way out.
 *
 * Anything else named — an `&mdash;` somebody typed by hand — is matched and
 * then deliberately left alone. Decoding those honestly would mean carrying
 * HTML5's two-thousand-entry reference table, and the markdown in this repo
 * writes literal characters rather than entities, so the table would earn its
 * weight only on a heading nobody has written yet. Numeric references need no
 * table and are handled below.
 */
const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  quot: '"',
};

/** One character reference of any shape that can reach a heading: `&amp;`,
 * `&#39;`, `&#x27;`. The name is matched loosely on purpose — an unknown one
 * is recognised here and then handed back untouched, rather than slipping
 * past the pattern. The two look the same in the output, but only the first
 * is a decision this file made. */
const ENTITY_PATTERN = /&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g;

/** The character a reference stands for, or `undefined` to leave it as it is. */
function decodeEntity(name: string): string | undefined {
  if (!name.startsWith("#")) return NAMED_ENTITIES[name];

  const hex = name[1] === "x" || name[1] === "X";
  const code = Number.parseInt(hex ? name.slice(2) : name.slice(1), hex ? 16 : 10);
  // Outside Unicode, or one half of a surrogate pair: `fromCodePoint` would
  // either throw or hand back a broken string. A heading has no business
  // holding one, so it keeps its escape rather than turning into U+FFFD.
  if (!Number.isInteger(code) || code <= 0 || code > 0x10ffff) return undefined;
  if (code >= 0xd800 && code <= 0xdfff) return undefined;
  return String.fromCodePoint(code);
}

/**
 * A heading's rendered HTML read back as its own text: no tags, and no
 * character references left standing in for what they escape.
 *
 * The references are decoded in one pass rather than two. A second sweep
 * would read the `&lt;` that `&amp;lt;` correctly decodes to and take it for
 * markup of its own, so a heading that shows `&lt;` to a reader would anchor
 * as though it said `<`.
 */
function plainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(ENTITY_PATTERN, (whole, name: string) => decodeEntity(name) ?? whole);
}
